import Phaser from 'phaser';
import { callGameStart } from '../utils/gamebox';

export class TitleScene extends Phaser.Scene {
  private howToPlayModal!: Phaser.GameObjects.Container | null;
  private howToPlayOverlay!: Phaser.GameObjects.Graphics | null;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    const { width, height } = this.scale;

    // 背景画像を表示
    const bgImage = this.add.image(width / 2, height / 2, 'bg_top');
    bgImage.setDisplaySize(width, height);
    bgImage.setDepth(0);

    // フォント読み込み完了を待ってからテキストを作成
    document.fonts.ready.then(() => {
      // フォントが読み込まれているか確認
      const fontLoaded = document.fonts.check('12px "Micro5"');
      console.log('フォント読み込み状態:', { fontLoaded, fonts: Array.from(document.fonts) });

      // タイトルロゴ表示（リサイズせず使用）
      const logoImage = this.add.image(width / 2, height / 3 + 20, 'logo');
      logoImage.setDepth(1);

      // サブタイトル
      const subtitleText = this.add
        .text(width / 2, height / 3 + 80, 'アルティメット バッター', {
          fontSize: '25px',
          fontFamily: 'YuseiMagic',
          color: '#ffffff', // ファミコン風の白
        })
        .setOrigin(0.5)
        .setDepth(1);

      // 開始ボタン（画像を使用、リサイズせず）
      const startButton = this.add.image(width / 2, height * 0.55, 'start');
      startButton.setDepth(1);
      startButton.setInteractive({ useHandCursor: true });

      // ホバー効果
      startButton.on('pointerover', () => {
        startButton.setTexture('start_hover');
      });
      startButton.on('pointerout', () => {
        startButton.setTexture('start');
      });

      // クリック/タップでゲーム開始
      startButton.on('pointerdown', () => {
        // ボタンタップ音を再生
        this.sound.play('button', { volume: 0.5 });
        // game_startを呼び出す（Tap to Startボタンタップ時）
        callGameStart(() => {
          // BGMを再生（ループ）
          const bgm = this.sound.add('bgm', { loop: true, volume: 0.5 });
          bgm.play();
          // BGMをシーン間で共有できるように保存
          (this as any).bgmSound = bgm;
          this.scene.start('GameScene');
        });
      });

      // 「あそび方」ボタンを追加（画像を使用、スタートボタンの下）
      const howToPlayButton = this.add.image(width / 2, height * 0.62, 'how-to-play');
      howToPlayButton.setDepth(1);
      howToPlayButton.setInteractive({ useHandCursor: true });

      // 遊び方ボタンのクリック処理
      howToPlayButton.on('pointerdown', () => {
        this.sound.play('button', { volume: 0.5 });
        this.showHowToPlayModal();
      });
    }).catch((error) => {
      console.error('フォント読み込みエラー:', error);
      // エラーが発生した場合でもテキストを表示（フォールバック）
      this.createTexts();
    });
  }

  private createTexts(): void {
    const { width, height } = this.scale;

    // タイトルロゴ表示（リサイズせず使用）
    const logoImage = this.add.image(width / 2, height / 3 + 20, 'logo');
    logoImage.setDepth(1);

    // サブタイトル
    const subtitleText = this.add
      .text(width / 2, height / 3 + 80, 'アルティメット バッター', {
        fontSize: '25px',
        fontFamily: 'DotGothic16',
        color: '#ffffff', // ファミコン風の白
      })
      .setOrigin(0.5)
      .setDepth(1);

    // 開始ボタン（画像を使用、リサイズせず）
    const startButton = this.add.image(width / 2, height * 0.55, 'start');
    startButton.setDepth(1);
    startButton.setInteractive({ useHandCursor: true });

    // ホバー効果
    startButton.on('pointerover', () => {
      startButton.setTexture('start_hover');
    });
    startButton.on('pointerout', () => {
      startButton.setTexture('start');
    });

    // クリック/タップでゲーム開始
    startButton.on('pointerdown', () => {
      // ボタンタップ音を再生
      this.sound.play('button', { volume: 0.5 });
      // BGMを再生（ループ）
      const bgm = this.sound.add('bgm', { loop: true, volume: 0.5 });
      bgm.play();
      // BGMをシーン間で共有できるように保存
      (this as any).bgmSound = bgm;
      this.scene.start('GameScene');
    });

    // 「あそび方」ボタンを追加（画像を使用、スタートボタンの下）
    const howToPlayButton = this.add.image(width / 2, height * 0.62, 'how-to-play');
    howToPlayButton.setDepth(1);
    howToPlayButton.setInteractive({ useHandCursor: true });

    // 遊び方ボタンのクリック処理
    howToPlayButton.on('pointerdown', () => {
      this.sound.play('button', { volume: 0.5 });
      this.showHowToPlayModal();
    });
  }

  private showHowToPlayModal(): void {
    const { width, height } = this.scale;

    // 既にモーダルが表示されている場合は何もしない
    if (this.howToPlayModal) {
      return;
    }

    // オーバーレイ（半透明の黒い背景）
    this.howToPlayOverlay = this.add.graphics();
    this.howToPlayOverlay.fillStyle(0x000000, 0.7);
    this.howToPlayOverlay.fillRect(0, 0, width, height);
    this.howToPlayOverlay.setDepth(100);
    this.howToPlayOverlay.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);

    // モーダルコンテナ
    this.howToPlayModal = this.add.container(width / 2, height / 2);
    this.howToPlayModal.setDepth(101);

    // モーダル背景（白い背景）
    const modalBg = this.add.graphics();
    const modalWidth = width * 0.85;
    const modalHeight = height * 0.75;
    modalBg.fillStyle(0xffffff, 1);
    modalBg.fillRoundedRect(-modalWidth / 2, -modalHeight / 2, modalWidth, modalHeight, 20);
    modalBg.setInteractive(new Phaser.Geom.Rectangle(-modalWidth / 2, -modalHeight / 2, modalWidth, modalHeight), Phaser.Geom.Rectangle.Contains);
    this.howToPlayModal.add(modalBg);

    // タイトル「あそび方」
    const titleText = this.add
      .text(0, -modalHeight / 2 + 40, 'あそび方', {
        fontSize: '40px',
        fontFamily: 'YuseiMagic',
        color: '#000000',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.howToPlayModal.add(titleText);

    // 遊び方の内容
    const contentText = this.add
      .text(0, -modalHeight / 2 + 100, 
        '【基本操作】\n\n' +
        '1. 「プレイボール」ボタンをタップ\n' +
        '   投球が開始されます\n\n' +
        '2. ボールが近づいたら画面をタップ\n' +
        '   タイミング判定が行われます\n\n' +
        '3. 角度バーが動いている間、押し続ける\n' +
        '   Best帯（緑色）で離すと飛距離が伸びます\n\n' +
        '4. 最適なタイミングで指を離す\n' +
        '   飛距離95m以上でホームラン！\n\n' +
        '【ゲームのルール】\n\n' +
        '• 3ストライクでゲームオーバー\n' +
        '• 空振りやファウルでストライク\n' +
        '• タイミングが合えば何打席でも続けられます\n' +
        '• 打席数に応じて球種が解放されます\n\n' +
        '【スコア】\n\n' +
        '• ホームラン: 飛距離 × 10点\n' +
        '• タイミングと角度が完璧だと高得点！',
        {
          fontSize: '24px',
          fontFamily: 'YuseiMagic',
          color: '#000000',
          align: 'left',
        }
      )
      .setOrigin(0.5, 0);
    contentText.setWordWrapWidth(modalWidth - 60);
    this.howToPlayModal.add(contentText);

    // 閉じるボタン
    const closeButton = this.add
      .text(modalWidth / 2 - 30, -modalHeight / 2 + 30, '×', {
        fontSize: '50px',
        fontFamily: 'YuseiMagic',
        color: '#000000',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    
    closeButton.on('pointerover', () => {
      closeButton.setStyle({ color: '#ff0000' });
    });
    closeButton.on('pointerout', () => {
      closeButton.setStyle({ color: '#000000' });
    });
    closeButton.on('pointerdown', () => {
      this.sound.play('button', { volume: 0.5 });
      this.closeHowToPlayModal();
    });
    this.howToPlayModal.add(closeButton);

    // オーバーレイをクリックしても閉じる
    this.howToPlayOverlay.on('pointerdown', () => {
      this.sound.play('button', { volume: 0.5 });
      this.closeHowToPlayModal();
    });
  }

  private closeHowToPlayModal(): void {
    if (this.howToPlayModal) {
      this.howToPlayModal.destroy();
      this.howToPlayModal = null;
    }
    if (this.howToPlayOverlay) {
      this.howToPlayOverlay.destroy();
      this.howToPlayOverlay = null;
    }
  }
}
